import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import WarningDialogIcon from "@entrilia/shared/icons/WarningDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { workflowTypeCaptionMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../../api/adminApi";
import ActionChecksAlert from "../../../../approvals/workflows/ActionChecksAlert";
import useClientContext from "../../../../../context/useClientContext";
import { pageRoutes } from "../../../../../routes";

interface Props {
  workflowId: string;
  templateId: string;
  workflowType: WorkflowType;
  onClose: () => void;
}

const restartWorkflow = withErrorHandling(adminApi.restartWorkflow);

const RestartWorkflowDialog = ({ workflowId, templateId, workflowType, onClose }: Props) => {
  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [restarting, setRestarting] = useState(false);
  const [comment, setComment] = useState("");

  const getRestartChecks = useCallback(() => adminApi.getWorkflowRestartChecks(workflowId), [workflowId]);
  const [restartChecks, restartChecksError, { isFetching: isCheckingRestart }] = useFetch(getRestartChecks);

  if (restartChecksError) {
    logError(restartChecksError, "[RestartWorkflowDialog] getWorkflowRestartChecks");
  }

  const restartWarnings = (restartChecks ?? []).map(({ message }) => message);

  const handleRestartWorkflow = async () => {
    const commentUserInput = comment.trim() || undefined;

    setRestarting(true);
    const [resp, error] = await restartWorkflow(workflowId, { commentUserInput });
    setRestarting(false);

    if (error) {
      logError(error, "[RestartWorkflowDialog] restartWorkflow");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Workflow restarted successfully");
    onClose();
    navigate(`/${clientCode}/${pageRoutes.workflows}/${pageRoutes.workflowRuns}/${templateId}/${resp.workflow.id}`);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Restart</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <WarningDialogIcon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            Restart {workflowTypeCaptionMap[workflowType] ?? workflowType}?
          </Typography>

          <Stack spacing={0.5}>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              The current run will be canceled and a new run will be started.
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              This might take a couple of minutes.
            </Typography>
          </Stack>

          {restartChecksError && (
            <ActionChecksAlert
              severity="warning"
              groups={[
                {
                  id: "restart-checks-failed",
                  messages: ["We couldn't check whether restarting will create a duplicate."],
                },
              ]}
            />
          )}
          {restartWarnings.length > 0 && (
            <ActionChecksAlert severity="warning" groups={[{ id: "restart-warnings", messages: restartWarnings }]} />
          )}

          <TextField
            multiline
            rows={4}
            spellCheck
            fullWidth
            placeholder="Add restart reason (optional)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelText="Close">
        <Button variant="contained" loading={restarting || isCheckingRestart} onClick={handleRestartWorkflow}>
          Restart Workflow
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default RestartWorkflowDialog;
